import { z } from "zod";

// ─── Units ───────────────────────────────────────────────────────────────────
export const unitIdSchema = z.string().uuid("ID de unidad inválido");

export const fuelTypeEnum   = ['diesel', 'gnc'] as const;
export const unitStatusEnum = ['active', 'in_repair', 'damaged', 'missing_docs'] as const;
export type FuelType   = (typeof fuelTypeEnum)[number];
export type UnitStatus = (typeof unitStatusEnum)[number];

// ─── Unit Specs ──────────────────────────────────────────────────────────────
export const createUnitSpecsSchema = z.object({
  tare:                 z.number().int().nullable().optional(),
  axles:                z.string().max(10).nullable().optional(),
  lengthM:              z.number().nullable().optional(),
  widthM:               z.number().nullable().optional(),
  heightM:              z.number().nullable().optional(),
  sealCount:            z.number().int().nullable().optional(),
  refrigerationUnitId:  z.string().uuid().nullable().optional(),
  tempSensorCount:      z.number().int().nullable().optional(),
  isMapped:             z.boolean().nullable().optional(),
  isCalibrated:         z.boolean().nullable().optional(),
});

export const updateUnitSpecsSchema = createUnitSpecsSchema.partial();

export const createUnitSchema = z.object({
  code:            z.string().min(1, "El código es requerido").max(20),
  name:            z.string().min(1, "El nombre es requerido").max(200),
  sgtCodes:        z.array(z.string().max(200)).nullable().optional(),
  unitTypeId:      z.string().uuid("Tipo de unidad inválido"),
  licensePlate:    z.string().max(20).nullable().optional(),
  vin:             z.string().max(100).nullable().optional(),
  brand:           z.string().max(100).nullable().optional(),
  model:           z.string().max(100).nullable().optional(),
  engineNumber:    z.string().max(100).nullable().optional(),
  unitNumber:      z.number().int().min(1).nullable().optional(),
  operatorId:      z.string().uuid().nullable().optional(),
  fuelType:        z.enum(['diesel', 'gnc']).nullable().optional(),
  manufactureYear: z.number().int().min(1000).max(2100).nullable().optional(),
  status:          z.enum(unitStatusEnum).optional(),
  hasSatellite:    z.boolean().optional(),
  location:        z.string().max(200).nullable().optional(),
  observations:    z.string().nullable().optional(),
  specs:           createUnitSpecsSchema.optional(),
});

export const updateUnitSchema = createUnitSchema.partial();

export const unitQuerySchema = z.object({
  deleted:        z.enum(["true", "false"]).optional(),
  unitTypeId:     z.string().uuid().optional(),
  unitTypeCodes:  z.string().optional(), // comma-separated type codes, e.g. "C,TRACTORGNC"
  search:         z.string().optional(),
  operatorIds:    z.string().optional(), // comma-separated UUIDs
  page:           z.coerce.number().int().min(1).optional().default(1),
  pageSize:       z.coerce.number().int().min(1).max(500).optional().default(25),
});

// ─── Types ───────────────────────────────────────────────────────────────────
export type UnitId         = z.infer<typeof unitIdSchema>;
export type CreateUnitSpecs = z.infer<typeof createUnitSpecsSchema>;
export type UpdateUnitSpecs = z.infer<typeof updateUnitSpecsSchema>;
export type CreateUnit     = z.infer<typeof createUnitSchema>;
export type UpdateUnit     = z.infer<typeof updateUnitSchema>;
export type UnitQuery      = z.infer<typeof unitQuerySchema>;
